import * as React from "react";
import { useContext, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { INavItem } from "src/components/layouts";

const navItemsEqual = (left: INavItem[], right: INavItem[]): boolean => {
    if (left === right) {
        return true;
    }

    if (left.length !== right.length) {
        return false;
    }

    return left.every((item, index) => {
        const other = right[index];
        return item.title === other.title
            && item.url === other.url
            && item.target === other.target
            && item.current === other.current
            && item.description === other.description
            && item.icon?.prefix === other.icon?.prefix
            && item.icon?.iconName === other.icon?.iconName;
    });
};

export interface IAppNavigationContext {
    isOpen: boolean;
    isDesktop: boolean;
    drawerItems: INavItem[];
    breadcrumbs: INavItem[];
    setBreadcrumbs(items: INavItem[]): void;
    clearBreadcrumbs(): void;
    setDrawerItems(items: INavItem[]): void;
    toggleDrawer(isOpen: boolean): void;
    closeMobileDrawer(): void;
}

const AppNavigationContext = React.createContext<
    IAppNavigationContext | undefined
>(undefined);

export const AppNavigationProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [desktopOpen, setDesktopOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [media] = useState(() => window.matchMedia("(min-width: 992px)"));
    const subscribe = React.useCallback((notify: () => void) => {
        const changed = () => {
            if (!media.matches) setMobileOpen(false);
            notify();
        };
        media.addEventListener("change", changed);
        return () => media.removeEventListener("change", changed);
    }, [media]);
    const isDesktop = useSyncExternalStore(subscribe, () => media.matches);
    const [drawerItems, setDrawerItems] = useState<INavItem[]>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<INavItem[]>([]);
    const currentDrawerItems = useRef(drawerItems);
    const activeLayout = useRef(isDesktop);
    useLayoutEffect(() => {
        activeLayout.current = isDesktop;
        currentDrawerItems.current = drawerItems;
    }, [drawerItems, isDesktop]);
    const isOpen = isDesktop ? desktopOpen : mobileOpen && drawerItems.length > 0;

    const actions = useMemo(
        () => ({
            setBreadcrumbs: (items: INavItem[]) => {
                setBreadcrumbs(previous => navItemsEqual(previous, items) ? previous : items);
            },
            clearBreadcrumbs: () => {
                setBreadcrumbs(previous => previous.length === 0 ? previous : []);
            },
            setDrawerItems: (items: INavItem[]) => {
                currentDrawerItems.current = items;
                setDrawerItems(previous => navItemsEqual(previous, items) ? previous : items);
                if (items.length === 0) setMobileOpen(false);
            },
            toggleDrawer: (open: boolean) => {
                if (activeLayout.current) setDesktopOpen(open);
                else setMobileOpen(open && currentDrawerItems.current.length > 0);
            },
            closeMobileDrawer: () => setMobileOpen(false),
        }),
        [],
    );

    const value = useMemo<IAppNavigationContext>(
        () => ({
            isOpen,
            isDesktop,
            drawerItems,
            breadcrumbs,
            ...actions,
        }),
        [
            isOpen,
            isDesktop,
            drawerItems,
            breadcrumbs,
            actions,
        ],
    );

    return (
        <AppNavigationContext.Provider value={value}>
            {children}
        </AppNavigationContext.Provider>
    );
};

export const useAppNavigation = (): IAppNavigationContext => {
    const context = useContext(AppNavigationContext);

    if (!context) {
        throw new Error(
            "useAppNavigation must be used within AppNavigationProvider",
        );
    }

    return context;
};

export default AppNavigationContext;
